<script>
  import { tick } from 'svelte';
  import { toasts, dialogState, _settleDialog } from '../../ui/dialogs.js';
  import { focusTrap } from '../actions/focusTrap.js';

  let inputValue = '';
  let inputEl;
  // Seed the input ONCE per dialog. The old reactive block re-ran on ANY
  // $dialogState write (a shared-state update elsewhere, a re-render) and wiped
  // whatever the user had typed so far.
  let seededFor = null;

  $: if ($dialogState && $dialogState.type === 'prompt' && seededFor !== $dialogState) {
    seededFor = $dialogState;
    inputValue = $dialogState.value || '';
  }
  $: if (!$dialogState) seededFor = null;
  $: if ($dialogState && inputEl) {
    tick().then(() => { if (inputEl) inputEl.focus(); });
  }

  function ok() {
    const s = $dialogState;
    if (!s) return;
    if (s.type === 'prompt') _settleDialog(s, inputValue);
    else if (s.type === 'confirm') _settleDialog(s, true);
    else _settleDialog(s, undefined);
  }
  function cancel() {
    const s = $dialogState;
    if (!s) return;
    if (s.type === 'prompt') _settleDialog(s, null);
    else if (s.type === 'confirm') _settleDialog(s, false);
    else _settleDialog(s, undefined);
  }
  function onKeydown(e) {
    if (!$dialogState) return;
    if (e.key === 'Escape') cancel();
    if (e.key === 'Enter' && $dialogState.type !== 'alert') ok();
  }
  function onBackdropClick(e) {
    if (e.target === e.currentTarget) cancel();
  }
</script>

<svelte:window on:keydown={onKeydown} />

<!-- Toast stack -->
{#if $toasts.length}
  <div class="toast-stack" aria-live="polite">
    {#each $toasts as t (t.id)}
      <div class="toast toast-{t.type}">{t.message}</div>
    {/each}
  </div>
{/if}

<!-- Modal dialog -->
{#if $dialogState}
  <div class="dlg-backdrop" role="presentation" on:click={onBackdropClick}>
    <div class="dlg" role="dialog" aria-modal="true" tabindex="-1" use:focusTrap>
      {#if $dialogState.title}
        <div class="dlg-title">{$dialogState.title}</div>
      {/if}
      <div class="dlg-message">{$dialogState.message}</div>
      {#if $dialogState.type === 'prompt'}
        <input
          class="dlg-input"
          bind:this={inputEl}
          bind:value={inputValue}
          placeholder={$dialogState.placeholder}
        />
      {/if}
      <div class="dlg-actions">
        {#if $dialogState.type !== 'alert'}
          <button class="dlg-btn" on:click={cancel}>{$dialogState.cancelText}</button>
        {/if}
        <button class="dlg-btn dlg-btn-primary" class:danger={$dialogState.danger} on:click={ok}>
          {$dialogState.okText}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .toast-stack {
    position: fixed;
    left: 50%;
    bottom: calc(24px + env(safe-area-inset-bottom));
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 12000;
    width: min(480px, calc(100vw - 32px));
    pointer-events: none;
  }
  .toast {
    padding: 12px 16px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.45;
    color: #f8fafc;
    background: rgba(15, 23, 42, 0.96);
    border: 1px solid rgba(148, 163, 184, 0.25);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
    animation: toast-in 0.22s ease-out;
    word-break: break-word;
  }
  .toast-success { border-color: rgba(52, 211, 153, 0.6); }
  .toast-error { border-color: rgba(248, 113, 113, 0.65); }
  @keyframes toast-in {
    from { transform: translateY(12px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  .dlg-backdrop {
    position: fixed;
    inset: 0;
    z-index: 11500;
    display: grid;
    place-items: center;
    padding: 20px;
    background: rgba(2, 6, 23, 0.7);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
  }
  .dlg {
    width: min(400px, 100%);
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
    animation: dlg-in 0.18s ease-out;
  }
  @keyframes dlg-in {
    from { transform: scale(0.96); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  .dlg-title {
    font-size: 15px;
    font-weight: 800;
    color: #f8fafc;
    margin-bottom: 8px;
  }
  .dlg-message {
    font-size: 13px;
    color: #cbd5e1;
    line-height: 1.55;
    white-space: pre-line;
    word-break: break-word;
  }
  .dlg-input {
    width: 100%;
    margin-top: 12px;
    background: #141d30;
    border: 1px solid #334155;
    border-radius: 10px;
    padding: 12px;
    color: #fff;
    font: inherit;
  }
  .dlg-input:focus { border-color: #10b981; outline: none; }
  .dlg-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 18px;
  }
  .dlg-btn {
    min-height: 44px;
    padding: 10px 18px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    color: #cbd5e1;
    background: transparent;
    border: 1px solid #334155;
  }
  .dlg-btn:active { background: #1e293b; }
  .dlg-btn-primary {
    color: #052e16;
    background: linear-gradient(135deg, #34d399, #10b981);
    border: none;
  }
  .dlg-btn-primary.danger {
    color: #fff;
    background: linear-gradient(135deg, #ef4444, #b91c1c);
  }
</style>
